import { useNavigate, Outlet, useLocation, Link } from "react-router-dom";
import {
  BackgroundImage,
  Text,
  Box,
  Card,
  Group,
  Stack,
  useMantineTheme,
  Button,
  Flex,
  Tabs,
  ScrollArea,
  useMantineColorScheme,
} from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { TargetIcon } from "@phosphor-icons/react";
import { useTour } from "@reactour/tour";
import { useEffect, useState } from "react";


export default function PathLayout() {
  const size = useElementSize();
  const { colorScheme } = useMantineColorScheme();
  const { setIsOpen, setCurrentStep } = useTour();

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMantineTheme();
  const [activeTab, setActiveTab] = useState<string | null>("new");
    
  useEffect(() => {
    setIsOpen(false); //explicitly close the tour
    setCurrentStep(3);
  }, []);

  // Update active tab based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === "/app/path/in-progress") {
      setActiveTab("inprogress");
    } else if (currentPath === "/app/path/completed") {
      setActiveTab("completed");
    } else if (currentPath === "/app/path" || currentPath === "/app/path/") {
      setActiveTab("new");
    }
  }, [location.pathname]);

  const tabBackgrounds: Record<string, string> = {
    new: "/assets/carousel-image3.jpg",
    inprogress: "/assets/In_Progress.jpg",
    completed: "/assets/carousel-image1.jpg",
  };
  const backgroundImageSrc = tabBackgrounds[activeTab ?? "new"];

  const backgroundImageStyle =
    activeTab === "new" || activeTab === "completed"
      ? {
          backgroundPosition: "center 55%",
          height: 180,
          width: "100%",
        }
      : {
          backgroundPosition: "center 60%",
          // backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          height: 180,
          width: "100%",
        };

  const tabRoutes: Record<string, string> = {
    new: "/app/path/",
    inprogress: "/app/path/in-progress",
    completed: "/app/path/completed",
  };

  return (
    <Box p={"md"}>
      <Card p={0} mb="md" radius="md" withBorder>
        <BackgroundImage
          src={backgroundImageSrc}
          radius="md"
          style={backgroundImageStyle}
        >
          <Box
            p="md"
            style={{
              position: "absolute",
              top: 93,
              left: 0,
              right: 0,
              height: "100%",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0) 100%)",
              borderRadius: "inherit",
              zIndex: 1,
            }}
          >
            <Group justify="space-between" align="flex-start" h="100%" p="0 1rem">
              <Stack gap={4}>
                <Text fw={700} size="xl" c="#FAFAFA">
                  Your Health Paths
                </Text>
                <Text size="md" c="#FAFAFA" fw={700}>
                  Personalized health journeys based on your data and goals.
                </Text>
              </Stack>
            </Group>
          </Box>
        </BackgroundImage>
      </Card>

      <Tabs
        value={activeTab}
        onChange={(tab) => {
          setActiveTab(tab);
          if (tab && tabRoutes[tab]) {
            navigate(tabRoutes[tab]);
          }
        }}
        data-tour="path-features"
        radius="md"
        variant="pills"
        defaultValue="new"
      >
        <Flex w={"100%"} p={0} align="center" gap="md" mb="md">
          <Group justify="space-between" align="center" w={"100%"}>
            <Tabs.List
              bg={
                colorScheme === "dark"
                  ? theme.colors.neutral[9]
                  : theme.colors.neutral[2]
              }
              style={{ borderRadius: "10px" }}
              p={"xs"}
            >
              <Tabs.Tab 
                value="new"
                style={{
                  backgroundColor: activeTab === "new" ? theme.colors.primary[7] : "transparent",
                  color: activeTab === "new" ? "white" : undefined,
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "new") {
                    e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                      ? "#555555"
                      : "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "new") {
                    e.currentTarget.style.backgroundColor = "transparent";
                  } else {
                    e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                  }
                }}
              >
                New
              </Tabs.Tab>
              <Tabs.Tab 
                value="inprogress"
                style={{
                  backgroundColor: activeTab === "inprogress" ? theme.colors.primary[7] : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "inprogress") {
                    e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                      ? "#555555"
                      : "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "inprogress") {
                    e.currentTarget.style.backgroundColor = "transparent";
                  } else {
                    e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                  }
                }}
              >
                In Progress
              </Tabs.Tab>
              <Tabs.Tab 
                value="completed"
                style={{
                  backgroundColor: activeTab === "completed" ? theme.colors.primary[7] : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "completed") {
                    e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                      ? "#555555"
                      : "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "completed") {
                    e.currentTarget.style.backgroundColor = "transparent";
                  } else {
                    e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                  }
                }}
              >
                Completed
              </Tabs.Tab>
            </Tabs.List>

            <Button
              radius="sm"
              leftSection={<TargetIcon size={16} />}
              component={Link}
              to={"/app/engagement-center"}
            >
              Check My Health Priorities
            </Button>
          </Group>
        </Flex>
      </Tabs>

      <Flex style={{ flexGrow: 1 }} direction={"column"} ref={size.ref}>
        <ScrollArea.Autosize h={"calc(100vh - 280px)"}>
          <Outlet />
        </ScrollArea.Autosize>
      </Flex>
    </Box>
  );
}
